import React from "react";
import General from "./general";
import ImageSlider from "../imageSlider";
import {useTranslation} from "next-i18next";
import List from "./nutrient/list";
import Ingredients from "./ingredients";
import Traces from "./traces";
import Stores from "./stores";
import Labels from "./labels";
import Images from "./images";
import Button from "../../button";
import Steps from "./steps";
import Success from "../../alert/success";
import Error from "../../alert/error";

/**
 *
 * @param title
 * @param buttonText
 * @param submitCallback
 * @param type
 * @param code
 * @param manufacturer
 * @param productName
 * @param amount
 * @param quantity
 * @param unit
 * @param images
 * @param allImages
 * @param nutrients
 * @param ingredients
 * @param traces
 * @param stores
 * @param labels
 * @param successMessage
 * @param errorMessage
 * @returns {JSX.Element}
 * @constructor
 */
export default function Base(
    { title, buttonText, submitCallback, type, code, manufacturer, productName, amount, quantity, unit, images, allImages, nutrients, ingredients, traces, stores, labels, successMessage, errorMessage }
)
{
    // init
    const { t } = useTranslation();

    /**
     *
     * @param event
     * @returns {Promise<void>}
     */
    async function submit(event) {
        return await submitCallback(event);
    }

    // render
    return (
        <>
            {/* Form */}
            <form className={'container p-4 col-12 col-sm-12 col-md-12 col-lg-10 col-xl-8 col-xxl-7 m-lg-0 ms-lg-5'} autoComplete="off">

                {/* Title */}
                <h1 className="text-center text-lg-start fw-bold mt-2 mb-3">{title}</h1>

                {/* Step by Step */}
                <Steps />

                {/* Images */}
                {images.length ? <div className="col-lg-6 col-xl-5 text-lg-start"><ImageSlider images={images} /></div> : ''}

                {/* Tabs */}
                <ul className="nav nav-tabs justify-content-center justify-content-lg-start mt-5" id={'nav-tab'} role={'tablist'}>

                    {/* General */}
                    <li className="nav-item" role={'presentation'}>
                        <button className="nav-link active" id="general-tab" data-bs-toggle="tab" data-bs-target="#general"
                                type="button" role="tab" aria-controls="general" aria-selected="true">{t('general')}
                        </button>
                    </li>

                    {/* Nutrients */}
                    <li className="nav-item" role={'presentation'}>
                        <button className="nav-link" id="nutrient-tab" data-bs-toggle="tab" data-bs-target="#nutrients"
                                type="button" role="tab" aria-controls="nutrients" aria-selected="false">{t('nutrients')}
                        </button>
                    </li>

                    {/* Ingredients */}
                    <li className="nav-item" role={'presentation'}>
                        <button className="nav-link" id="ingredient-tab" data-bs-toggle="tab" data-bs-target="#ingredients"
                                type="button" role="tab" aria-controls="ingredients" aria-selected="false">{t('ingredients')}
                        </button>
                    </li>

                    {/* Traces */}
                    <li className="nav-item" role={'presentation'}>
                        <button className="nav-link" id="trace-tab" data-bs-toggle="tab" data-bs-target="#traces"
                                type="button" role="tab" aria-controls="traces" aria-selected="false">{t('traces')}
                        </button>
                    </li>

                    {/* Stores */}
                    <li className="nav-item" role={'presentation'}>
                        <button className="nav-link" id="store-tab" data-bs-toggle="tab" data-bs-target="#stores"
                                type="button" role="tab" aria-controls="stores" aria-selected="false">{t('stores')}
                        </button>
                    </li>

                    {/* Labels */}
                    <li className="nav-item" role={'presentation'}>
                        <button className="nav-link" id="label-tab" data-bs-toggle="tab" data-bs-target="#labels"
                                type="button" role="tab" aria-controls="labels" aria-selected="false">{t('labels')}
                        </button>
                    </li>

                    {/* Images */}
                    <li className="nav-item" role={'presentation'}>
                        <button className="nav-link" id="image-tab" data-bs-toggle="tab" data-bs-target="#images"
                                type="button" role="tab" aria-controls="images" aria-selected="false">{t('images')}
                        </button>
                    </li>
                </ul>

                {/* Tab-Content */}
                <div className="tab-content mt-2 pt-2" id="myTabContent">
                    {/* General */}
                    <div className="tab-pane fade show active" id="general" role="tabpanel" aria-labelledby="general-tab">
                        <General
                            type={type}
                            code={code}
                            manufacturer={manufacturer}
                            productName={productName}
                            amount={amount}
                            quantity={quantity}
                            unit={unit}
                        />
                    </div>

                    {/* Nutrients */}
                    <div className="tab-pane fade" id="nutrients" role="tabpanel" aria-labelledby="nutrient-tab">
                        <List nutrients={nutrients} />
                    </div>

                    {/* Ingredients */}
                    <div className="tab-pane fade" id="ingredients" role="tabpanel" aria-labelledby="ingredient-tab">
                        <Ingredients ingredients={ingredients} />
                    </div>

                    {/* Traces */}
                    <div className="tab-pane fade" id="traces" role="tabpanel" aria-labelledby="trace-tab">
                        <Traces traces={traces} />
                    </div>

                    {/* Stores */}
                    <div className="tab-pane fade" id="stores" role="tabpanel" aria-labelledby="store-tab">
                        <Stores stores={stores} />
                    </div>

                    {/* Labels */}
                    <div className="tab-pane fade" id="labels" role="tabpanel" aria-labelledby="label-tab">
                        <Labels labels={labels} />
                    </div>

                    {/* Images */}
                    <div className="tab-pane fade" id="images" role="tabpanel" aria-labelledby="image-tab">
                        <Images images={allImages} />
                    </div>
                </div>

                {/* Alert-Messages */}
                {successMessage ? <Success message={successMessage} /> : (errorMessage ? <Error message={errorMessage} /> : '')}

                {/* Submit-Button */}
                <div className={'m-auto ms-lg-0 me-lg-0 mt-4 mb-5 col-8 col-sm-5 col-md-4 col-lg-3 col-xl-4 col-xxl-4'}>
                    <Button id={'product-form-submit-btn'} outline={false} color={'primary'} text={buttonText} callback={submit} hideTextOnMobile={false} submitOnlyOnce={true}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor"
                             className="bi bi-pencil-square align-text-bottom" viewBox="0 0 16 16">
                            <path
                                d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                            <path fillRule="evenodd"
                                  d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
                        </svg>
                    </Button>
                </div>
            </form>
        </>
    );
}